import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { Copy, ExternalLink, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { fullProfileQuery } from "@/lib/people";
import { useMyReferralCode } from "@/hooks/useReferral";
import { ShareButtons, shareUrl } from "@/components/share-buttons";
import { useT } from "@/i18n";

/**
 * The creator's own public page link, always carrying their referral code.
 * Sharing the page is the primary referral channel: anyone who signs up after
 * landing on it is credited to the page owner.
 */
export function MyPageLink({ compact = false }: { compact?: boolean }) {
  const { user } = useAuth();
  const { data: profile } = useQuery(fullProfileQuery(user?.id));
  const refCode = useMyReferralCode();
  const { t } = useT();

  const slug = profile?.slug || profile?.handle || null;
  const name = profile?.display_name || profile?.handle || "me";

  if (!profile) return null;

  if (!slug) {
    return (
      <div className="card-surface p-5">
        <p className="flex items-center gap-2 font-display text-lg font-bold">
          <UserRound className="size-5 text-primary" /> {t("account.page.title")}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("account.page.noSlug")}
        </p>
        <Button asChild variant="hot" size="sm" className="mt-3">
          <Link to="/app/profile">{t("account.page.choose")}</Link>
        </Button>
      </div>
    );
  }

  const path = `/user/${slug}`;
  const url = shareUrl(path, refCode);

  return (
    <div className="card-surface p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-display text-lg font-bold">
          <UserRound className="size-5 text-primary" /> {t("account.page.title")}
        </p>
        <Button asChild variant="ghost" size="sm">
          <a href={path} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="size-4" /> {t("account.page.open")}
          </a>
        </Button>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        {t("account.page.desc")}
      </p>
      <div className="mt-3 flex gap-2">
        <Input readOnly value={url} />
        <Button
          variant="hot"
          onClick={() => {
            void navigator.clipboard.writeText(url);
            toast.success(t("account.page.copied"));
          }}
        >
          <Copy className="size-4" /> {t("account.page.copy")}
        </Button>
      </div>
      {!compact && (
        <ShareButtons
          className="mt-4"
          path={path}
          refCode={refCode}
          title={t("account.page.shareTitle", { name })}
        />
      )}
      {profile.pro_enabled && (
        <p className="mt-3 text-xs text-muted-foreground">
          {t("account.page.proPage")} <span className="font-medium">/pro/{slug}</span>
        </p>
      )}
    </div>
  );
}
